import { useState } from 'react';
import { downloadFile, ApiError } from '../lib/api';

/**
 * Authenticated download button. Replaces plain `<a href=…>` which browsers
 * can't attach our Bearer token to, causing the backend to redirect to /login.
 *
 * Usage:
 *   <DownloadButton
 *     href={`/dissertation/${id}/download/pdf`}
 *     filename={`Dissertation_${id}.pdf`}
 *     icon="fa-file-pdf"
 *     label="Download PDF"
 *     className="btn btn-cta"
 *   />
 */
export default function DownloadButton({
  href,
  filename,
  icon = 'fa-download',
  label = 'Download',
  className = 'btn btn-outline-primary',
  onError,
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function click(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await downloadFile(href, filename);
    } catch (ex) {
      const msg = ex instanceof ApiError ? ex.message : 'Download failed';
      setErr(msg);
      if (onError) onError(msg);
      // Surface as a toast-lite: auto-clear after 4s
      setTimeout(() => setErr(null), 4000);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={click}
        disabled={busy}
        title={err || label || 'Download'}
      >
        <i className={`fas ${busy ? 'fa-spinner fa-spin' : icon}${label ? ' me-1' : ''}`}></i>
        {busy ? ' Preparing…' : label ? ` ${label}` : ''}
      </button>
      {err && (
        <span className="text-danger small ms-2" style={{ whiteSpace: 'nowrap' }}>
          {err}
        </span>
      )}
    </>
  );
}
